import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import {
  getUserProfile,
  getCompanyByDomain,
  getMyCompanyAdminEmails,
  checkCompanyNameExists,
  getPolicyDocuments,
  savePolicyDocuments,
  saveSingleVendor,
  completeOnboarding,
  searchCompanies,
  registerCompany,
} from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import usePageMeta from '../hooks/usePageMeta';
import { buildVendorWebsiteUrl } from '../lib/vendorUtils';
import { trackGoogleAdsConversion } from '../lib/googleAds';
import '../styles/onboarding.css';

function Onboarding() {
  usePageMeta({
    title: 'Onboarding',
    noindex: true,
  });

  const { user } = useAuth0();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Part 1: Organization Info
  const [companyName, setCompanyName] = useState('');
  const [uniqueName, setUniqueName] = useState('');
  const [showUniqueNameField, setShowUniqueNameField] = useState(false);
  const [description, setDescription] = useState('');
  const [industry, setIndustry] = useState('');
  const [country, setCountry] = useState('');

  // Part 2: Policy Documents
  const [documents, setDocuments] = useState([]);
  const [existingPolicyDocuments, setExistingPolicyDocuments] = useState([]);

  // Part 3: Vendor Declaration
  const [vendors, setVendors] = useState([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorSuggestions, setSuggestions] = useState([]);
  const [currentVendor, setCurrentVendor] = useState({
    name: '',
    importance: 2,
    contact_email: '',
    website_url: '',
  });
  const [vendorEmailLocal, setVendorEmailLocal] = useState('');
  const [vendorDomain, setVendorDomain] = useState('');
  const [companyId, setCompanyId] = useState(null);

  // Part 4: External Integrity Consent
  const [externalScanConsent, setExternalScanConsent] = useState(false);

  // Access Control
  const [accessControl, setAccessControl] = useState('domain-limited');
  const [adminEmails, setAdminEmails] = useState([]);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [adminEmailError, setAdminEmailError] = useState('');

  const emailDomain = user?.email?.split('@')[1] || '';
  const [companyDomain, setCompanyDomain] = useState(emailDomain);
  const userEmail = user?.email || '';

  // Load pre-fill data on mount
  useEffect(() => {
    const loadPrefilledData = async () => {
      try {
        const profile = await getUserProfile();
        const linkedCompany = profile?.company;

        if (linkedCompany && linkedCompany.onboarded === false) {
          setCompanyName(linkedCompany.name || '');
          setDescription(linkedCompany.description || '');
          setIndustry(linkedCompany.industry || '');
          setCountry(linkedCompany.country || '');
          setCompanyDomain(linkedCompany.domain || emailDomain);
          setCompanyId(linkedCompany.id);

          if (linkedCompany.admin_controlled) {
            setAccessControl('admin');
            const linkedAdminEmails = await getMyCompanyAdminEmails();
            setAdminEmails((linkedAdminEmails || []).filter((email) => email !== userEmail));
          } else {
            setAccessControl('domain-limited');
          }
          return;
        }

        if (!emailDomain) return;

        const { company, adminEmails } = await getCompanyByDomain(emailDomain);
        if (company) {
          setCompanyName(company.name || '');
          setDescription(company.description || '');
          setIndustry(company.industry || '');
          setCountry(company.country || '');
          setCompanyDomain(company.domain || emailDomain);
          setCompanyId(company.id);
          
          // Set access control based on admin_controlled flag
          if (company.admin_controlled) {
            setAccessControl('admin');
            // Pre-fill admin emails, filtering out current user
            setAdminEmails(adminEmails.filter(email => email !== userEmail));
          } else {
            setAccessControl('domain-limited');
          }
        }
      } catch (err) {
        console.error('Error loading pre-filled data:', err);
      }
    };

    loadPrefilledData();
  }, [emailDomain, userEmail]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [step]);

  useEffect(() => {
    if (!companyId) return;

    const loadPolicyDocuments = async () => {
      try {
        const savedDocuments = await getPolicyDocuments();
        setExistingPolicyDocuments(savedDocuments || []);
      } catch (err) {
        console.error('Error loading existing policy documents:', err);
      }
    };

    loadPolicyDocuments();
  }, [companyId]);

  // Check company name uniqueness
  const checkCompanyNameUniqueness = async (name, isUniqueNameField = false) => {
    try {
      const normalized = name.toLowerCase();
      const exists = await checkCompanyNameExists(normalized);

      if (isUniqueNameField) {
        // When checking unique name, only show error if it exists
        if (exists) {
          setError('This name already exists. Please choose another.');
        } else {
          setError('');
        }
      } else {
        // When checking primary name, toggle the unique name field
        if (exists) {
          setShowUniqueNameField(true);
          setError('This company name already exists. Please provide a unique name.');
        } else {
          setShowUniqueNameField(false);
          setError('');
        }
      }
    } catch (err) {
      console.error('Error checking company name:', err);
    }
  };

  const handleCompanyNameChange = (e) => {
    const name = e.target.value;
    setCompanyName(name);
    if (name.length > 2) {
      checkCompanyNameUniqueness(name);
    } else {
      setShowUniqueNameField(false);
    }
  };

  const handleUniqueNameChange = (e) => {
    const name = e.target.value;
    setUniqueName(name);
    if (name.length > 2) {
      checkCompanyNameUniqueness(name, true);
    } else {
      setError('');
    }
  };

  // Vendor search functionality
  const handleVendorSearch = async (query) => {
    setVendorSearch(query);
    if (query.length >= 2) {
      try {
        const results = await searchCompanies(query);
        // Filter out suggestions (don't show score in vendor selection)
        setSuggestions(results.map(r => ({ name: r.normalized_name, domain: r.domain })));
      } catch (err) {
        console.error('Error searching vendors:', err);
      }
    } else {
      setSuggestions([]);
    }
  };

  const addVendor = async (vendorOption) => {
    if (!currentVendor.name) {
      setError('Please select a vendor');
      return;
    }

    if (!currentVendor.contact_email && !currentVendor.website_url) {
      setError('Please enter either a contact email or a website URL for the vendor');
      return;
    }

    if (!companyId) {
      setError('Company must be registered before adding vendors');
      return;
    }

    // Save vendor immediately to database
    setLoading(true);
    try {
      const newVendor = {
        name: vendorOption?.name || currentVendor.name,
        importance: currentVendor.importance,
        contact_email: currentVendor.contact_email,
        website_url: currentVendor.website_url,
      };

      // Save to database immediately
      await saveSingleVendor(companyId, newVendor);

      // Update local state for UI
      setVendors([...vendors, newVendor]);
      setCurrentVendor({ name: '', importance: 2, contact_email: '', website_url: '' });
      setVendorEmailLocal('');
      setVendorDomain('');
      setVendorSearch('');
      setSuggestions([]);
      setError('');
    } catch (err) {
      console.error('Error adding vendor:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to add vendor. Please try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const removeVendor = (index) => {
    setVendors(vendors.filter((_, i) => i !== index));
  };

  const handleDocumentUpload = (e) => {
    if (existingPolicyDocuments.length > 0) {
      setError('A policy document already exists. Delete it from Policy Maturity before uploading a replacement.');
      e.target.value = '';
      return;
    }

    const files = Array.from(e.target.files);
    const pdfFiles = files.filter(file => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'));

    if (pdfFiles.length === 0) {
      setError('Please upload a PDF file');
      return;
    }

    if (pdfFiles.length > 1) {
      setError('Only one policy document can be uploaded per company');
    } else {
      setError('');
    }

    const file = pdfFiles[0];
    setDocuments([
      {
        file,
        fileName: file.name,
        fileSize: file.size,
        type: 'cybersecurity_policy',
      },
    ]);
  };

  const removeDocument = (index) => {
    setDocuments(documents.filter((_, i) => i !== index));
  };

  const addAdminEmail = () => {
    setAdminEmailError('');
    
    if (!newAdminEmail.trim()) {
      setAdminEmailError('Please enter an email address');
      return;
    }

    const emailRegex = /^\S+@\S+\.\S+$/;
    if (!emailRegex.test(newAdminEmail)) {
      setAdminEmailError('Please enter a valid email address');
      return;
    }

    if (adminEmails.includes(newAdminEmail)) {
      setAdminEmailError('This email has already been added');
      return;
    }

    if (newAdminEmail === userEmail) {
      setAdminEmailError('Your email is already added as the admin');
      return;
    }

    setAdminEmails([...adminEmails, newAdminEmail]);
    setNewAdminEmail('');
  };

  const removeAdminEmail = (emailToRemove) => {
    setAdminEmails(adminEmails.filter(email => email !== emailToRemove));
  };

  const handleNextStep = async () => {
    setError('');

    if (step === 1) {
      if (!companyName.trim()) {
        setError('Company name is required');
        return;
      }
      if (showUniqueNameField && !uniqueName.trim()) {
        setError('Please provide a unique name for your organization');
        return;
      }
      if (showUniqueNameField && error) {
        setError('Please provide a unique name that does not already exist');
        return;
      }
      // Register company before moving to step 2
      await registerCompanyForOnboarding();
    } else if (step === 2) {
      setStep(3);
    } else if (step === 3) {
      if (!externalScanConsent) {
        setError('Please confirm that you authorize the external integrity scan');
        return;
      }
      setStep(4);
    } else if (step === 4) {
      if (documents.length === 0 && existingPolicyDocuments.length === 0) {
        setError('Please upload one cybersecurity policy PDF to complete onboarding');
        return;
      }

      if (documents.length > 0) {
        await savePolicyDocumentsForOnboarding();
      }
      await submitOnboarding();
    }
  };

  const registerCompanyForOnboarding = async () => {
    setLoading(true);
    try {
      const displayName = showUniqueNameField ? uniqueName : companyName;
      const isAdminControlled = accessControl === 'admin';
      const allAdminEmails = isAdminControlled ? [userEmail, ...adminEmails] : [];
      
      const companyResponse = await registerCompany({
        name: displayName,
        domain: companyDomain,
        websiteUrl: null,
        description: description || null,
        industry: industry || null,
        country: country || null,
        admin_controlled: isAdminControlled,
        admin_emails: allAdminEmails,
      });
      
      setCompanyId(companyResponse.company.id);
      trackGoogleAdsConversion('company_domain_submitted', { once: true });
      setStep(2);
    } catch (err) {
      console.error('Error registering company:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to register company. Please try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const savePolicyDocumentsForOnboarding = async () => {
    setLoading(true);
    try {
      if (documents.length > 0) {
        const savedDocuments = await savePolicyDocuments(companyId, documents);
        setExistingPolicyDocuments(savedDocuments || []);
        setDocuments([]);
      }
    } catch (err) {
      console.error('Error saving policy documents:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to save policy documents. Please try again.';
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const submitOnboarding = async () => {
    setLoading(true);
    try {
      // All vendors have already been saved as they were added
      // The policy document has already been saved on the final step.
      // Just mark onboarding as complete
      await completeOnboarding(companyId, externalScanConsent);
      trackGoogleAdsConversion('onboarding_complete');
      trackGoogleAdsConversion('profile_published', { once: true });

      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('Error completing onboarding:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to complete onboarding. Please try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const previewName = (showUniqueNameField && uniqueName.trim()) || companyName.trim();
  const previewInitials = previewName
    ? previewName
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join('')
    : 'CO';

  return (
    <div className="onboarding-page">
      <Header />

      <main className={`onboarding-main step-${step}`}>
        <div className="onboarding-container">
          {/* Progress Indicator */}
          <div className="progress-indicator">
            <div className={`step ${step >= 1 ? 'active' : ''}`}>1</div>
            <div className={`divider ${step >= 2 ? 'active' : ''}`}></div>
            <div className={`step ${step >= 2 ? 'active' : ''}`}>2</div>
            <div className={`divider ${step >= 3 ? 'active' : ''}`}></div>
            <div className={`step ${step >= 3 ? 'active' : ''}`}>3</div>
            <div className={`divider ${step >= 4 ? 'active' : ''}`}></div>
            <div className={`step ${step >= 4 ? 'active' : ''}`}>4</div>
          </div>

          {/* Step 1: Organization Information */}
          {step === 1 && (
            <div className="onboarding-step">
              <h1>Organization Information</h1>
              <p className="step-description">
                Help us learn more about your organization to complete your profile.
              </p>

              <div className="organization-step-layout">
                <div className="organization-form">
                  <div className="form-group">
                    <label>Company Name *</label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={handleCompanyNameChange}
                      placeholder="Enter your company name"
                      className="form-input"
                    />
                    {showUniqueNameField && (
                      <div className="form-group">
                        <label className="warning">Unique Display Name *</label>
                        <input
                          type="text"
                          value={uniqueName}
                          onChange={handleUniqueNameChange}
                          placeholder="Enter a unique name for your organization"
                          className="form-input"
                        />
                        <small>A company with this name already exists. Please provide a unique display name.</small>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Website Domain</label>
                    <input
                      type="text"
                      value={companyDomain}
                      disabled
                      className="form-input disabled"
                      placeholder="Your domain"
                    />
                    <small>This is detected from your linked company (or your email domain) and cannot be changed.</small>
                  </div>

                  <div className="form-group">
                    <label>Description (Optional)</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="What does your organization do?"
                      className="form-textarea"
                      rows="4"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Industry (Optional)</label>
                      <input
                        type="text"
                        value={industry}
                        onChange={(e) => setIndustry(e.target.value)}
                        placeholder="e.g., Finance, Healthcare, Tech"
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label>Country (Optional)</label>
                      <input
                        type="text"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        placeholder="e.g., United States"
                        className="form-input"
                      />
                    </div>
                  </div>

                  {/* Access Control Toggle */}
                  <div className="form-group">
                    <label>Access Control</label>
                    <div className="access-control-toggle">
                      <button
                        type="button"
                        className={`toggle-option ${accessControl === 'domain-limited' ? 'active' : ''}`}
                        onClick={() => {
                          setAccessControl('domain-limited');
                          setAdminEmails([]);
                          setAdminEmailError('');
                        }}
                      >
                        <span className="material-symbols-outlined">security</span>
                        Domain-Limited Access
                      </button>
                      <button
                        type="button"
                        className={`toggle-option ${accessControl === 'admin' ? 'active' : ''}`}
                        onClick={() => setAccessControl('admin')}
                      >
                        <span className="material-symbols-outlined">supervised_user_circle</span>
                        Admin-Controlled Access
                      </button>
                    </div>
                    <small>
                      {accessControl === 'domain-limited'
                        ? 'Anyone with your company domain email can access the dashboard'
                        : 'Only users you specify can access the dashboard'}
                    </small>
                  </div>

                  {/* Admin Emails Section */}
                  {accessControl === 'admin' && (
                    <div className="form-group">
                      <label>Authorized Admins</label>

                      {/* Current User */}
                      <div className="admin-email-item current-user">
                        <div className="email-content">
                          <span className="email">{userEmail}</span>
                          <span className="badge">You (Admin)</span>
                        </div>
                      </div>

                      {/* List of other admins */}
                      {adminEmails.length > 0 && (
                        <div className="admin-emails-list">
                          {adminEmails.map((email) => (
                            <div key={email} className="admin-email-item">
                              <div className="email-content">
                                <span className="email">{email}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeAdminEmail(email)}
                                className="btn-remove-email"
                              >
                                <span className="material-symbols-outlined">close</span>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add new admin email */}
                      <div className="add-admin-email-group">
                        <input
                          type="email"
                          value={newAdminEmail}
                          onChange={(e) => {
                            setNewAdminEmail(e.target.value);
                            setAdminEmailError('');
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addAdminEmail();
                            }
                          }}
                          placeholder="Enter admin email"
                          className="form-input"
                        />
                        <button
                          type="button"
                          onClick={addAdminEmail}
                          className="btn btn-secondary"
                        >
                          Add
                        </button>
                      </div>
                      {adminEmailError && <div className="error-text">{adminEmailError}</div>}
                    </div>
                  )}
                </div>

                <aside className="company-profile-preview" aria-label="Company profile preview">
                  <div className="company-profile-preview__identity">
                    <div className="company-profile-preview__avatar" aria-hidden="true">
                      {previewInitials}
                    </div>
                    <div>
                      <h2>{previewName || 'Your company'}</h2>
                      <p>{companyDomain || 'company-domain.com'}</p>
                    </div>
                  </div>

                  <p className={`company-profile-preview__description ${description.trim() ? '' : 'is-placeholder'}`}>
                    {description.trim() || 'Your company description will appear here as you type.'}
                  </p>

                  <div className="company-profile-preview__details">
                    <div>
                      <span>Industry</span>
                      <strong>{industry.trim() || 'Not specified'}</strong>
                    </div>
                    <div>
                      <span>Country</span>
                      <strong>{country.trim() || 'Not specified'}</strong>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          )}

          {/* Step 4: Policy Documents */}
          {step === 4 && (
            <div className="onboarding-step">
              <h1>Policy Documents</h1>
              <p className="step-description">
                Upload one cybersecurity policy PDF to improve your Total Security Score and complete onboarding.
              </p>

              <div className="form-group">
                <label>Upload Policy Document *</label>
                <div className={`file-upload-area ${existingPolicyDocuments.length > 0 ? 'is-disabled' : ''}`}>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleDocumentUpload}
                    id="file-input"
                    className="file-input"
                    disabled={existingPolicyDocuments.length > 0}
                  />
                  <label htmlFor="file-input" className="file-upload-label">
                    <span className="material-symbols-outlined">cloud_upload</span>
                    <p>
                      {existingPolicyDocuments.length > 0
                        ? 'Policy document already uploaded'
                        : 'Click to upload or drag and drop'}
                    </p>
                    <small>
                      {existingPolicyDocuments.length > 0
                        ? 'Only one policy document is allowed per company'
                        : 'One PDF file only'}
                    </small>
                  </label>
                </div>

                {existingPolicyDocuments.length > 0 && (
                  <div className="document-list">
                    <h4>Uploaded Document</h4>
                    <div className="document-item">
                      <span className="material-symbols-outlined">description</span>
                      <div className="document-info">
                        <p className="document-name">{existingPolicyDocuments[0].filename}</p>
                        <small>This document will be used for your policy maturity analysis.</small>
                      </div>
                      <span className="material-symbols-outlined policy-document-complete">check_circle</span>
                    </div>
                  </div>
                )}

                {documents.length > 0 && (
                  <div className="document-list">
                    <h4>Ready to Upload</h4>
                    {documents.map((doc, index) => (
                      <div key={index} className="document-item">
                        <span className="material-symbols-outlined">description</span>
                        <div className="document-info">
                          <p className="document-name">{doc.fileName}</p>
                          <small>{(doc.fileSize / 1024).toFixed(2)} KB</small>
                        </div>
                        <button
                          onClick={() => removeDocument(index)}
                          className="btn-remove"
                        >
                          <span className="material-symbols-outlined">close</span>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Vendor Declaration */}
          {step === 2 && (
            <div className="onboarding-step">
              <h1>Vendor Declaration</h1>
              <p className="step-description">
                Declare your third-party vendors to improve network risk assessment.
              </p>

              <div className="form-group">
                <label>Add Vendors</label>
                <div className="vendor-input-group">
                  <div className="vendor-form-section">
                    <label className="vendor-field-label" htmlFor="vendor-name">Vendor</label>
                    <div className="vendor-search">
                      <input
                        id="vendor-name"
                        type="text"
                        value={vendorSearch}
                        onChange={(e) => handleVendorSearch(e.target.value)}
                        placeholder="Search or enter vendor name"
                        className="form-input"
                      />
                      {vendorSearch.trim().length >= 2 && vendorSearch.trim() !== currentVendor.name && (
                        <div className="vendor-suggestions">
                          {vendorSuggestions.map((suggestion, idx) => (
                            <div
                              key={idx}
                              className="vendor-suggestion"
                              onClick={() => {
                                setVendorSearch(suggestion.name);
                                setCurrentVendor({
                                  ...currentVendor,
                                  name: suggestion.name,
                                  contact_email: '',
                                  website_url: buildVendorWebsiteUrl(suggestion.domain),
                                });
                                setVendorDomain(suggestion.domain || '');
                                setVendorEmailLocal('');
                                setSuggestions([]);
                              }}
                            >
                              <span className="material-symbols-outlined">graph_5</span>
                              <span>{suggestion.name} ({suggestion.domain})</span>
                            </div>
                          ))}
                          <div
                            className="vendor-suggestion"
                            onClick={() => {
                              const displayName = vendorSearch.charAt(0).toUpperCase() + vendorSearch.slice(1);
                              setVendorSearch(displayName);
                              setCurrentVendor({
                                ...currentVendor,
                                name: displayName,
                                contact_email: '',
                                website_url: '',
                              });
                              setVendorDomain('');
                              setVendorEmailLocal('');
                              setSuggestions([]);
                            }}
                          >
                            <span className="material-symbols-outlined">graph_5</span>
                            <span>{vendorSearch.charAt(0).toUpperCase() + vendorSearch.slice(1)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="vendor-tier-layout">
                    <div className="vendor-form-section">
                      <label className="vendor-field-label" htmlFor="vendor-tier">Criticality</label>
                      <select
                        id="vendor-tier"
                        value={currentVendor.importance}
                        onChange={(e) =>
                          setCurrentVendor({ ...currentVendor, importance: parseInt(e.target.value) })
                        }
                        className="form-select"
                      >
                        <option value={1}>Tier 1 (Lowest)</option>
                        <option value={2}>Tier 2 (Important)</option>
                        <option value={3}>Tier 3 (Most Critical)</option>
                      </select>
                    </div>

                    <div className="tier-guide">
                      <p className="tier-guide-title">Tier Guide</p>
                      <div className="tier-guide-item"><strong>1</strong><span>Limited or non-critical data handling.</span></div>
                      <div className="tier-guide-item"><strong>2</strong><span>Important operational dependency with meaningful data access.</span></div>
                      <div className="tier-guide-item"><strong>3</strong><span>Mission-critical dependency or highly sensitive data access.</span></div>
                    </div>
                  </div>

                  <div className="vendor-contact-section">
                    <div className="vendor-contact-heading">
                      <p>Contact</p>
                      <span>Provide either an email or website.</span>
                    </div>
                    <div className="vendor-contact-grid">
                      <div className="vendor-contact-field">
                        <label className="vendor-field-label" htmlFor="vendor-email">Security email</label>
                        {vendorDomain ? (
                          <div className="vendor-email-input">
                            <input
                              id="vendor-email"
                              type="text"
                              value={vendorEmailLocal}
                              onChange={(e) => {
                                const local = e.target.value.split('@')[0];
                                setVendorEmailLocal(local);
                                const fullEmail = local ? `${local}@${vendorDomain}` : '';
                                setCurrentVendor({ ...currentVendor, contact_email: fullEmail });
                              }}
                              placeholder="e.g. security"
                              className="form-input"
                            />
                            <span className="email-domain">@{vendorDomain}</span>
                          </div>
                        ) : (
                          <input
                            id="vendor-email"
                            type="email"
                            value={currentVendor.contact_email}
                            onChange={(e) =>
                              setCurrentVendor({ ...currentVendor, contact_email: e.target.value })
                            }
                            placeholder="security@example.com"
                            className="form-input"
                          />
                        )}
                      </div>

                      <div className="vendor-contact-field">
                        <label className="vendor-field-label" htmlFor="vendor-website">Website URL</label>
                        <input
                          id="vendor-website"
                          type="url"
                          value={currentVendor.website_url}
                          onChange={(e) =>
                            setCurrentVendor({ ...currentVendor, website_url: e.target.value })
                          }
                          readOnly={Boolean(vendorDomain)}
                          placeholder={vendorDomain ? 'Prefilled from company domain' : 'https://example.com'}
                          className="form-input"
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => addVendor({ name: vendorSearch })}
                    className="btn btn-primary vendor-add-button"
                  >
                    Add Vendor
                  </button>
                </div>
              </div>

              {vendors.length > 0 && (
                <div className="vendor-list">
                  <h4>Declared Vendors ({vendors.length})</h4>
                  {vendors.map((vendor, index) => (
                    <div key={index} className="vendor-item">
                      <div className="vendor-details">
                        <p className="vendor-name">{vendor.name}</p>
                        <div className="vendor-meta">
                          <span className="importance">Tier: {vendor.importance}</span>
                          {vendor.contact_email && (
                            <span className="contact">{vendor.contact_email}</span>
                          )}
                          {!vendor.contact_email && vendor.website_url && (
                            <span className="contact">{vendor.website_url}</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => removeVendor(index)}
                        className="btn-remove"
                      >
                        <span className="material-symbols-outlined">close</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Reassurance message */}
              <div className="vendors-saved-notice">
                <span className="material-symbols-outlined">check_circle</span>
                <p>Don't worry! Your vendors are saved as you add them. You can leave and come back anytime to continue.</p>
              </div>
            </div>
          )}

          {/* Step 3: External Integrity */}
          {step === 3 && (
            <div className="onboarding-step">
              <h1>External Integrity</h1>
              <p className="step-description">
                Risqly reviews public-facing security signals to create your External Integrity score.
              </p>

              <div className="external-integrity-panel">
                <div className="external-integrity-intro">
                  <span className="material-symbols-outlined" aria-hidden="true">travel_explore</span>
                  <div>
                    <h2>A non-intrusive view of your public security posture</h2>
                    <p>
                      The scan uses your company domain and publicly available information. It does not
                      sign in, bypass access controls, or inspect private systems.
                    </p>
                  </div>
                </div>

                <div className="external-scan-grid">
                  <div className="external-scan-item">
                    <span className="material-symbols-outlined" aria-hidden="true">language</span>
                    <div>
                      <h3>Website security</h3>
                      <p>HTTPS redirects, security headers, cookie protections, and server information exposure.</p>
                    </div>
                  </div>
                  <div className="external-scan-item">
                    <span className="material-symbols-outlined" aria-hidden="true">encrypted</span>
                    <div>
                      <h3>TLS and certificates</h3>
                      <p>Certificate trust and expiry, supported TLS version, and transport encryption.</p>
                    </div>
                  </div>
                  <div className="external-scan-item">
                    <span className="material-symbols-outlined" aria-hidden="true">router</span>
                    <div>
                      <h3>Network exposure</h3>
                      <p>Common publicly reachable ports that may increase external exposure.</p>
                    </div>
                  </div>
                  <div className="external-scan-item">
                    <span className="material-symbols-outlined" aria-hidden="true">alternate_email</span>
                    <div>
                      <h3>Email authentication</h3>
                      <p>Public SPF and DMARC records used to protect your domain from spoofing.</p>
                    </div>
                  </div>
                  <div className="external-scan-item">
                    <span className="material-symbols-outlined" aria-hidden="true">dns</span>
                    <div>
                      <h3>DNS integrity</h3>
                      <p>Whether DNSSEC is enabled for stronger domain-name protection.</p>
                    </div>
                  </div>
                  <div className="external-scan-item">
                    <span className="material-symbols-outlined" aria-hidden="true">policy</span>
                    <div>
                      <h3>Public vulnerability signals</h3>
                      <p>Recent public CVE records associated with your company name, treated as risk signals.</p>
                    </div>
                  </div>
                </div>

                <label className="external-scan-consent" htmlFor="external-scan-consent">
                  <input
                    id="external-scan-consent"
                    type="checkbox"
                    checked={externalScanConsent}
                    onChange={(e) => {
                      setExternalScanConsent(e.target.checked);
                      setError('');
                    }}
                  />
                  <span>
                    I confirm that I am authorized to represent this organization and agree that Risqly
                    may scan its public-facing domain and public security signals to calculate and
                    periodically update its External Integrity score.
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && <div className="error-message">{error}</div>}

          {/* Navigation Buttons */}
          <div className="onboarding-actions">
            {step > 1 && (
              <button
                onClick={() => {
                  setStep(step - 1);
                }}
                className="btn btn-secondary"
                disabled={loading}
              >
                Previous
              </button>
            )}
            <button
              onClick={handleNextStep}
              className={`btn ${
                (step === 2 && vendors.length === 0) || (step === 3 && !externalScanConsent)
                  ? 'btn-secondary'
                  : 'btn-primary'
              }`}
              disabled={loading}
            >
              {loading
                ? 'Processing...'
                : step === 2 && vendors.length === 0
                  ? "I don't have any vendors"
                  : step === 2 || step === 3
                    ? 'Continue'
                    : step === 4
                      ? documents.length > 0
                        ? 'Upload and Complete'
                        : 'Complete Onboarding'
                    : 'Next'}
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default Onboarding;
